import React, { useContext, useMemo, useState, useEffect, useId } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  PieChart, 
  Pie, 
  Cell,
  CartesianGrid,
  Area,
  AreaChart,
  LabelList,
} from 'recharts';
import { UserGroupIcon, GlobeAltIcon, ChartPieIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { ThemeContext } from '../context/ThemeContext';
import CurrencyViewToggle from './CurrencyViewToggle';
import { formatMoney } from '../utils/money';
import { VISTA_CONSOLIDADO } from '../utils/currencyView';
import ResponsiveChartBox from './charts/ResponsiveChartBox';

const useViewportWidth = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return width;
};

const COLORES = ['#1F80FF', '#5CA3FF', '#004AAD', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8'];

const DashboardCharts = ({
  topClientes,
  ingresosPais,
  loading,
  onChartClick,
  resumen,
  impagas,
  evolucionMensual,
  comparacionMeses,
  inPanel = false,
  currency = 'ARS',
  currencyVista = VISTA_CONSOLIDADO,
  onCurrencyVistaChange,
  currencyMonedas = [],
  showCurrencyToggle = false,
  showIngresosPorPais = true,
}) => {
  const { modoOscuro } = useContext(ThemeContext);
  const viewportWidth = useViewportWidth();
  const ventasGradientId = useId().replace(/:/g, '');

  const chartCard = (extra = '') => {
    const base = inPanel
      ? (modoOscuro ? 'rounded-xl p-3.5 sm:p-4 bg-gray-800/50 border border-gray-700/50' : 'rounded-xl p-3.5 sm:p-4 bg-gray-50 border border-gray-200')
      : (modoOscuro ? 'rounded-2xl p-4 sm:p-5 border bg-gray-900/80 border-gray-700/50' : 'rounded-2xl p-4 sm:p-5 border bg-white border-gray-100 shadow-sm');
    return `${base} ${extra}`.trim();
  };

  const areaChartHeight = viewportWidth >= 1536 ? 170 : viewportWidth >= 1024 ? 185 : 200;
  const pieChartHeight = viewportWidth >= 1536 ? 200 : 220;

  // Cálculo de estado de cobranza
  const datosCobranza = useMemo(() => {
    const total = resumen?.totalIngresos || 0;
    const pendiente = impagas?.monto || 0;
    const cobrado = total - pendiente;
    return [
      { nombre: 'Cobrado', valor: cobrado, color: '#1F80FF' },
      { nombre: 'Pendiente', valor: pendiente, color: '#F59E42' }
    ];
  }, [resumen, impagas]);

  // Usar datos reales de comparación de meses
  const comparacionMes = useMemo(() => {
    return comparacionMeses || {
      mesActual: 0,
      mesAnterior: 0,
      variacion: 0
    };
  }, [comparacionMeses]);

  const formatCurrency = (amount) => formatMoney(amount, currency || 'ARS').replace(',00', '');

  const formatAxis = (value) => {
    const prefix = currency === 'USD' ? 'US$' : currency === 'EUR' ? '€' : '$';
    const n = Number(value) || 0;
    const abs = Math.abs(n);
    if (abs >= 1000000) return `${prefix}${(n / 1000000).toFixed(1).replace('.', ',')} M`;
    if (abs >= 1000) return `${prefix}${Math.round(n / 1000)} mil`;
    return `${prefix}${Math.round(n)}`;
  };

  const paisChart = useMemo(() => {
    const raw = [...(ingresosPais || [])]
      .filter((item) => (item.total || 0) !== 0)
      .sort((a, b) => (b.total || 0) - (a.total || 0));
    const total = raw.reduce((sum, item) => sum + (item.total || 0), 0);
    const top = raw.slice(0, 5);
    const resto = raw.slice(5).reduce((sum, item) => sum + (item.total || 0), 0);
    const rows = top.map((item, index) => ({
      pais: item.pais || item._id || 'Sin país',
      total: item.total || 0,
      cantidad: item.cantidadFacturas || 0,
      pct: total > 0 ? (item.total / total) * 100 : 0,
      color: COLORES[index % COLORES.length],
    }));
    if (resto > 0) {
      rows.push({
        pais: 'Resto',
        total: resto,
        cantidad: raw.slice(5).reduce((sum, item) => sum + (item.cantidadFacturas || 0), 0),
        pct: total > 0 ? (resto / total) * 100 : 0,
        color: '#94A3B8',
      });
    }
    return { rows, total, count: raw.length };
  }, [ingresosPais]);

  const evoInsight = useMemo(() => {
    const data = (evolucionMensual && evolucionMensual.length > 0) ? evolucionMensual : [];
    const sum = data.reduce((acc, row) => acc + (row.ventas || 0), 0);
    const last = data[data.length - 1];
    const prev = data[data.length - 2];
    const delta = last && prev && prev.ventas
      ? ((last.ventas - prev.ventas) / prev.ventas) * 100
      : null;
    return { data, sum, last, prev, delta };
  }, [evolucionMensual]);

  const hasChartData =
    (topClientes?.length > 0)
    || (ingresosPais?.length > 0)
    || (evolucionMensual?.length > 0)
    || (resumen?.totalIngresos > 0);

  if (loading && !hasChartData) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        <div className={`rounded-2xl p-6 border animate-pulse ${
          modoOscuro ? 'bg-gray-900/50 border-gray-700/50' : 'bg-white border-gray-100'
        }`}>
          <div className={modoOscuro ? 'h-6 bg-gray-700/50 rounded mb-6' : 'h-6 bg-gray-200 rounded mb-6'}></div>
          <div className={modoOscuro ? 'h-64 bg-gray-700/50 rounded' : 'h-64 bg-gray-200 rounded'}></div>
        </div>
        <div className={`rounded-2xl p-6 border animate-pulse ${
          modoOscuro ? 'bg-gray-900/50 border-gray-700/50' : 'bg-white border-gray-100'
        }`}>
          <div className={modoOscuro ? 'h-6 bg-gray-700/50 rounded mb-6' : 'h-6 bg-gray-200 rounded mb-6'}></div>
          <div className={modoOscuro ? 'h-64 bg-gray-700/50 rounded' : 'h-64 bg-gray-200 rounded'}></div>
        </div>
        <div className={`rounded-2xl p-6 border animate-pulse ${
          modoOscuro ? 'bg-gray-900/50 border-gray-700/50' : 'bg-white border-gray-100'
        }`}>
          <div className={modoOscuro ? 'h-6 bg-gray-700/50 rounded mb-6' : 'h-6 bg-gray-200 rounded mb-6'}></div>
          <div className={modoOscuro ? 'h-64 bg-gray-700/50 rounded' : 'h-64 bg-gray-200 rounded'}></div>
        </div>
        <div className={`rounded-2xl p-6 border sm:col-span-2 xl:col-span-2 animate-pulse ${
          modoOscuro ? 'bg-gray-900/50 border-gray-700/50' : 'bg-white border-gray-100'
        }`}>
          <div className={modoOscuro ? 'h-6 bg-gray-700/50 rounded mb-6' : 'h-6 bg-gray-200 rounded mb-6'}></div>
          <div className={modoOscuro ? 'h-40 bg-gray-700/50 rounded' : 'h-40 bg-gray-200 rounded'}></div>
        </div>
        <div className={`rounded-2xl p-6 border animate-pulse ${
          modoOscuro ? 'bg-gray-900/50 border-gray-700/50' : 'bg-white border-gray-100'
        }`}>
          <div className={modoOscuro ? 'h-6 bg-gray-700/50 rounded mb-6' : 'h-6 bg-gray-200 rounded mb-6'}></div>
          <div className={modoOscuro ? 'h-64 bg-gray-700/50 rounded' : 'h-64 bg-gray-200 rounded'}></div>
        </div>
      </div>
    );
  }

  const datosClientes = topClientes?.map((item, index) => ({
    ...item,
    color: COLORES[index % COLORES.length]
  })) || [];

  const evolucionSpan = showIngresosPorPais ? 'sm:col-span-2 xl:col-span-2' : 'sm:col-span-2 xl:col-span-3';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
      {showIngresosPorPais && (
      <div
        className={`${chartCard()} ${onChartClick ? 'cursor-pointer hover:border-[#1F80FF]/40 transition-colors' : ''}`}
        onClick={() => onChartClick?.('paises')}
        title="Ver ingresos por país"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
              <GlobeAltIcon className="h-5 w-5 text-[#1F80FF]" />
            </div>
            <div className="min-w-0">
              <h3 className={`text-sm sm:text-base font-semibold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>Ingresos por país</h3>
              <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
                {paisChart.count > 0
                  ? `${paisChart.count} país${paisChart.count !== 1 ? 'es' : ''} · ${formatCurrency(paisChart.total)}`
                  : 'De dónde vinieron las ventas'}
              </p>
            </div>
          </div>
        </div>

        {paisChart.rows.length > 0 ? (
          <ResponsiveChartBox height={210}>
            {(width, height) => (
              <BarChart
                width={width}
                height={height}
                data={paisChart.rows}
                layout="vertical"
                margin={{ top: 4, right: 36, left: 4, bottom: 0 }}
              >
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="pais"
                  width={72}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: modoOscuro ? '#9CA3AF' : '#6B7280', fontSize: 11 }}
                />
                <Tooltip
                  cursor={{ fill: modoOscuro ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const row = payload[0]?.payload || {};
                    return (
                      <div className={`rounded-xl border px-3 py-2 text-xs shadow-lg ${
                        modoOscuro ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                      }`}>
                        <p className="font-semibold">{label}</p>
                        <p className={modoOscuro ? 'text-gray-200' : 'text-gray-600'}>
                          {row.cantidad ? `${row.cantidad} ventas · ` : ''}
                          {formatCurrency(row.total)} · {Number(row.pct || 0).toFixed(1)}%
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="total" radius={[0, 6, 6, 0]} barSize={16} isAnimationActive={false}>
                  {paisChart.rows.map((row) => (
                    <Cell key={row.pais} fill={row.color} />
                  ))}
                  <LabelList
                    dataKey="pct"
                    position="right"
                    formatter={(value) => `${Number(value).toFixed(0)}%`}
                    style={{ fill: modoOscuro ? '#D1D5DB' : '#4B5563', fontSize: 10, fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            )}
          </ResponsiveChartBox>
        ) : (
          <div className={`h-[210px] flex items-center justify-center ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="text-center px-4">
              <GlobeAltIcon className={`h-10 w-10 mx-auto mb-2 ${modoOscuro ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className="text-sm font-medium">Todavía no hay ventas por país</p>
              <p className="text-xs mt-1">Cuando registres una venta con país, aparece acá.</p>
            </div>
          </div>
        )}
      </div>
      )}

      <div className={chartCard(evolucionSpan)}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
              <ArrowTrendingUpIcon className="h-5 w-5 text-[#1F80FF]" />
            </div>
            <div className="min-w-0">
              <h3 className={`text-sm sm:text-base font-semibold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>Evolución mensual</h3>
              <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
                Últimos {evoInsight.data.length || 6} meses
              </p>
            </div>
          </div>
          {evoInsight.last && evoInsight.sum > 0 && (
            <div className="text-right shrink-0">
              <p className={`text-sm font-semibold tabular-nums ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>
                {formatCurrency(evoInsight.last.ventas)}
              </p>
              {evoInsight.delta != null ? (
                <p className={`text-[11px] font-semibold ${evoInsight.delta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {evoInsight.delta >= 0 ? '+' : ''}{evoInsight.delta.toFixed(1)}% vs {evoInsight.prev?.mes}
                </p>
              ) : (
                <p className={`text-[11px] ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>{evoInsight.last.mes}</p>
              )}
            </div>
          )}
        </div>

        {evoInsight.data.length > 0 && evoInsight.sum > 0 ? (
          <ResponsiveChartBox height={areaChartHeight}>
            {(width, height) => (
            <AreaChart
              width={width}
              height={height}
              data={evoInsight.data}
              margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id={ventasGradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1F80FF" stopOpacity={0.28} />
                  <stop offset="95%" stopColor="#1F80FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={modoOscuro ? '#374151' : '#E5E7EB'} vertical={false} />
              <XAxis
                dataKey="mes"
                axisLine={false}
                tickLine={false}
                tick={{ fill: modoOscuro ? '#9CA3AF' : '#6B7280', fontSize: 11 }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={52}
                tick={{ fill: modoOscuro ? '#9CA3AF' : '#6B7280', fontSize: 11 }}
                tickFormatter={formatAxis}
              />
              <Tooltip
                cursor={{ stroke: modoOscuro ? 'rgba(255,255,255,0.12)' : 'rgba(31,128,255,0.25)' }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0]?.payload || {};
                  return (
                    <div className={`rounded-xl border px-3 py-2 text-xs shadow-lg ${
                      modoOscuro ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                    }`}>
                      <p className="font-semibold">{label}</p>
                      <p className={modoOscuro ? 'text-gray-200' : 'text-gray-600'}>
                        {formatCurrency(row.ventas)}
                        {row.cantidad ? ` · ${row.cantidad} ventas` : ''}
                      </p>
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="ventas"
                stroke="#1F80FF"
                strokeWidth={2.5}
                fill={`url(#${ventasGradientId})`}
                dot={{ r: 3.5, fill: '#1F80FF', stroke: modoOscuro ? '#111827' : '#ffffff', strokeWidth: 2 }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            </AreaChart>
            )}
          </ResponsiveChartBox>
        ) : (
          <div className={`h-[185px] flex items-center justify-center ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="text-center px-4">
              <ArrowTrendingUpIcon className={`h-10 w-10 mx-auto mb-2 ${modoOscuro ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className="text-sm font-medium">Todavía no hay evolución para mostrar</p>
              <p className="text-xs mt-1">Cuando haya ventas en el período, vas a ver el mes a mes.</p>
            </div>
          </div>
        )}
      </div>

      {/* Gráfico de dona - Estado de Cobranza */}
      <div className={chartCard()}>
        <div className="flex items-center gap-3 mb-3 sm:mb-4">
          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${modoOscuro ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
            <ChartPieIcon className="h-5 w-5 text-[#1F80FF]" />
          </div>
          <div>
            <h3 className={`text-sm sm:text-base font-semibold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>Estado de Cobranza</h3>
            <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>Cobrado vs Pendiente</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6 min-w-0">
          <div className="w-[48%] max-w-[200px] shrink-0 min-w-[120px]">
          <ResponsiveChartBox height={160}>
            {(width, height) => (
            <PieChart width={width} height={height}>
              <Pie
                data={datosCobranza}
                dataKey="valor" 
                nameKey="nombre" 
                cx="50%" 
                cy="50%" 
                innerRadius={35}
                outerRadius={55} 
                paddingAngle={2}
              >
                {datosCobranza.map((entry, index) => (
                  <Cell 
                    key={index} 
                    fill={entry.color} 
                    stroke={modoOscuro ? '#111827' : '#ffffff'}
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [formatCurrency(value), name]}
                contentStyle={{
                  backgroundColor: modoOscuro ? '#1F2937' : '#ffffff',
                  border: `1px solid ${modoOscuro ? '#4B5563' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                  padding: '8px 12px',
                  fontSize: '12px'
                }}
                itemStyle={{
                  color: modoOscuro ? '#F9FAFB' : '#111827'
                }}
                labelStyle={{
                  color: modoOscuro ? '#F9FAFB' : '#111827',
                  fontWeight: 500
                }}
              />
            </PieChart>
            )}
          </ResponsiveChartBox>
          </div>
          
          <div className="flex-1 min-w-0 space-y-3">
            {datosCobranza.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }}></div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>{item.nombre}</p>
                  <p className={`text-sm font-semibold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(item.valor)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI - Comparación Mes Actual vs Anterior */}
      <div className={chartCard()}>
        <div className="flex items-center gap-3 mb-3 sm:mb-4">
          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${modoOscuro ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
            <ArrowTrendingUpIcon className="h-5 w-5 text-[#1F80FF]" />
          </div>
          <div>
            <h3 className={`text-sm sm:text-base font-semibold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>vs Mes Anterior</h3>
            <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>Comparación mensual</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Mes Actual</p>
            <p className={`text-xl font-bold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(comparacionMes.mesActual)}
            </p>
          </div>
          
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            comparacionMes.variacion >= 0 
              ? 'bg-green-500/10' 
              : 'bg-red-500/10'
          }`}>
            <svg 
              className={`w-4 h-4 ${comparacionMes.variacion >= 0 ? 'text-green-500' : 'text-red-500'}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              {comparacionMes.variacion >= 0 ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              )}
            </svg>
            <span className={`text-sm font-medium ${
              comparacionMes.variacion >= 0 ? 'text-green-500' : 'text-red-500'
            }`}>
              {comparacionMes.variacion >= 0 ? '+' : ''}{comparacionMes.variacion.toFixed(1)}%
            </span>
            <span className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
              vs mes ant.
            </span>
          </div>
          
          <div>
            <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Mes Anterior</p>
            <p className={`text-sm ${modoOscuro ? 'text-gray-300' : 'text-gray-600'}`}>
              {formatCurrency(comparacionMes.mesAnterior)}
            </p>
          </div>
        </div>
      </div>

      {/* Gráfico de dona - Distribución por cliente */}
      <div
        className={chartCard()}
        onClick={() => onChartClick('clientes')}
        title="Ver distribución completa de clientes"
      >
        <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
              <UserGroupIcon className="h-5 w-5 text-[#1F80FF]" />
            </div>
            <div className="min-w-0">
              <h3 className={`text-sm sm:text-base font-semibold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>Distribución por Cliente</h3>
              <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
                Ventas por cliente
                {currencyVista === VISTA_CONSOLIDADO && showCurrencyToggle ? ' · total' : currencyVista !== VISTA_CONSOLIDADO ? ` · ${currency}` : ''}
              </p>
            </div>
          </div>
          {showCurrencyToggle && onCurrencyVistaChange && (
            <div
              className="shrink-0"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <CurrencyViewToggle
                value={currencyVista}
                onChange={onCurrencyVistaChange}
                monedas={currencyMonedas}
                modoOscuro={modoOscuro}
              />
            </div>
          )}
        </div>
        
        {datosClientes.length > 0 ? (
          <ResponsiveChartBox height={pieChartHeight}>
            {(width, height) => (
            <PieChart width={width} height={height}>
              <Pie 
                data={datosClientes} 
                dataKey="total" 
                nameKey="cliente" 
                cx="50%" 
                cy="50%" 
                innerRadius={55}
                outerRadius={90} 
                paddingAngle={3}
              >
                {datosClientes.map((entry, index) => (
                  <Cell 
                    key={entry.cliente || index} 
                    fill={entry.color} 
                    stroke={modoOscuro ? '#111827' : '#ffffff'}
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  formatCurrency(value),
                  props?.payload?.cliente || 'Cliente'
                ]}
                contentStyle={{
                  backgroundColor: modoOscuro ? '#1F2937' : '#ffffff',
                  border: `1px solid ${modoOscuro ? '#4B5563' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                  padding: '8px 12px',
                  fontSize: '12px'
                }}
                itemStyle={{
                  color: modoOscuro ? '#F9FAFB' : '#111827'
                }}
                labelStyle={{
                  color: modoOscuro ? '#F9FAFB' : '#111827',
                  fontWeight: 500
                }}
              />
            </PieChart>
            )}
          </ResponsiveChartBox>
        ) : (
          <div className={`h-64 flex items-center justify-center ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="text-center">
              <UserGroupIcon className={`h-12 w-12 mx-auto mb-3 ${modoOscuro ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className="text-sm">No hay datos disponibles</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCharts;
